import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSetAtom, useStore, useAtomValue, useAtom } from 'jotai'
import { getDefaultStore } from 'jotai'
import { toast } from 'sonner'

import { PairingScreen } from './pairing/PairingScreen'
import { ConnectionBanner } from './components/ConnectionBanner'
import { EmailVerificationStep } from './components/onboarding'
import { getStoredConnection, storeConnection, parseWsUrl, connect, disconnect, clearStoredConnection, setupElectronApi, getClient, setClientWorkspaceId, addConnectionToHistory } from './transport/connection'

import { useTheme } from '@/hooks/useTheme'
import type { ThemeOverrides } from '@config/theme'
import type {
  Session,
  Workspace,
  SessionEvent,
  Message,
  FileAttachment,
  StoredAttachment,
  PermissionRequest,
  CredentialRequest,
  CredentialResponse,
  SessionStatus,
  NewChatActionParams,
  ContentBadge,
  LlmConnectionWithStatus,
  SetupNeeds,
  PermissionModeState,
  AskUserQuestionRequest,
  AskUserQuestionAnswer,
} from '@/shared/types'
import type { SessionOptions, SessionOptionUpdates } from './hooks/useSessionOptions'
import { defaultSessionOptions, mergeSessionOptions } from './hooks/useSessionOptions'
import { generateMessageId } from '@/shared/types'
import { useEventProcessor } from './event-processor/useEventProcessor'
import type { AgentEvent, Effect } from './event-processor/types'
import { AppShell } from '@/components/app-shell/AppShell'
import type { AppShellContextType } from '@/context/AppShellContext'
import { TooltipProvider } from '@craft-agent/ui'
import { FocusProvider } from '@/context/FocusContext'
import { ModalProvider } from '@/context/ModalContext'
import { DismissibleLayerProvider } from '@/context/DismissibleLayerContext'
import { useSession } from '@/hooks/useSession'
import { NavigationProvider } from '@/contexts/NavigationContext'
import { navigate, routes } from './lib/navigate'
import { stripMarkdown } from './utils/text'
import { extractWorkspaceSlugFromPath } from '@craft-agent/shared/utils/workspace-slug'
import { toAgentFileAttachment, isAbsoluteFilesystemPath } from '@craft-agent/shared/utils'
import { DEFAULT_THINKING_LEVEL } from '@craft-agent/shared/agent/thinking-levels'
import {
  initializeSessionsAtom,
  addSessionAtom,
  removeSessionAtom,
  updateSessionAtom,
  sessionAtomFamily,
  sessionMetaMapAtom,
  sessionIdsAtom,
  backgroundTasksAtomFamily,
  extractSessionMeta,
  windowWorkspaceIdAtom,
  type SessionMeta,
  type BackgroundTask,
} from '@/atoms/sessions'
import { sourcesAtom } from '@/atoms/sources'
import { skillsAtom } from '@/atoms/skills'
import { extractBadges } from '@/lib/mentions'
import {
  ShikiThemeProvider,
  PlatformProvider,
  ImagePreviewOverlay,
  PDFPreviewOverlay,
  DocxPreviewOverlay,
  CodePreviewOverlay,
  JSONPreviewOverlay,
} from '@craft-agent/ui'
import { Globe } from 'lucide-react'
import { useLinkInterceptor, type FilePreviewState } from '@/hooks/useLinkInterceptor'
import { useTransportConnectionState } from '@/hooks/useTransportConnectionState'
import { TransportConnectionBanner, shouldShowTransportConnectionBanner } from '@/components/app-shell/TransportConnectionBanner'
import { getFileManagerName } from '@/lib/platform'
import { ActionRegistryProvider } from '@/actions'
import { OfflineBanner } from '@/components/OfflineBanner'
import { EditableMarkdownOverlay } from '@/components/markdown-editor'
import { useVirtualKeyboard } from '@/hooks/useVirtualKeyboard'
import { Loader2 } from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

type AppState = 'pairing' | 'connecting' | 'connected'
type JotaiStore = ReturnType<typeof getDefaultStore>

// ─── Background Task Event Handler (same as Electron) ──────────────────────────

function handleBackgroundTaskEvent(
  store: JotaiStore,
  sessionId: string,
  event: { type: string },
  agentEvent: unknown
): void {
  const evt = agentEvent as Record<string, unknown>
  const backgroundTasksAtom = backgroundTasksAtomFamily(sessionId)
  const getTasks = () => store.get(backgroundTasksAtom) as BackgroundTask[]

  if (event.type === 'task_backgrounded' && 'taskId' in evt && 'toolUseId' in evt) {
    const currentTasks = getTasks()
    if (!currentTasks.some(t => t.toolUseId === evt.toolUseId)) {
      store.set(backgroundTasksAtom, [
        ...currentTasks,
        {
          id: evt.taskId as string,
          type: 'agent' as const,
          toolUseId: evt.toolUseId as string,
          startTime: Date.now(),
          elapsedSeconds: 0,
          intent: evt.intent as string | undefined,
        },
      ])
    }
  } else if (event.type === 'shell_backgrounded' && 'shellId' in evt && 'toolUseId' in evt) {
    const currentTasks = getTasks()
    if (!currentTasks.some(t => t.toolUseId === evt.toolUseId)) {
      store.set(backgroundTasksAtom, [
        ...currentTasks,
        {
          id: evt.shellId as string,
          type: 'shell' as const,
          toolUseId: evt.toolUseId as string,
          startTime: Date.now(),
          elapsedSeconds: 0,
          intent: evt.intent as string | undefined,
        },
      ])
    }
  } else if (event.type === 'task_progress' && 'toolUseId' in evt && 'elapsedSeconds' in evt) {
    const currentTasks = getTasks()
    store.set(backgroundTasksAtom, currentTasks.map(t =>
      t.toolUseId === evt.toolUseId
        ? { ...t, elapsedSeconds: evt.elapsedSeconds as number }
        : t
    ))
  } else if (event.type === 'task_completed' && 'taskId' in evt) {
    const currentTasks = getTasks()
    store.set(backgroundTasksAtom, currentTasks.filter(t => t.id !== evt.taskId))
  } else if (event.type === 'shell_killed' && 'shellId' in evt) {
    const currentTasks = getTasks()
    store.set(backgroundTasksAtom, currentTasks.filter(t => t.id !== evt.shellId))
  } else if (event.type === 'tool_result' && 'toolUseId' in evt) {
    const result = typeof evt.result === 'string' ? evt.result : JSON.stringify(evt.result)
    const isBackgroundingResult = result && (
      /agentId:\s*[a-zA-Z0-9_-]+/.test(result) ||
      /shell_id:\s*[a-zA-Z0-9_-]+/.test(result) ||
      /"backgroundTaskId":\s*"[a-zA-Z0-9_-]+"/.test(result)
    )
    if (!isBackgroundingResult) {
      const currentTasks = getTasks()
      store.set(backgroundTasksAtom, currentTasks.filter(t => t.toolUseId !== evt.toolUseId))
    }
  }
}

// ─── Main App Component ────────────────────────────────────────────────────────

export default function App() {
  const [appState, setAppState] = useState<AppState>('pairing')
  const [workspaceId, setWorkspaceId] = useState<string | undefined>()

  // Auto-connect with URL param or stored connection on launch
  useEffect(() => {
    const finalizeConnectedWorkspace = async (
      host: string,
      port: number,
      protocol: 'ws' | 'wss' = 'ws',
      preferredWorkspaceId?: string,
    ): Promise<string | undefined> => {
      try {
        const workspaces = await window.electronAPI.getWorkspaces()
        const workspaceId = preferredWorkspaceId ?? workspaces?.[0]?.id

        if (workspaceId) {
          // Keep handshake workspace + server routing in sync before first turn.
          setClientWorkspaceId(workspaceId)
          await window.electronAPI.switchWorkspace(workspaceId).catch(() => {})
          storeConnection({ host, port, workspaceId, protocol })
        } else {
          storeConnection({ host, port, protocol })
        }

        addConnectionToHistory({ host, port, protocol })
        return workspaceId
      } catch {
        storeConnection({ host, port, protocol })
        addConnectionToHistory({ host, port, protocol })
        return preferredWorkspaceId
      }
    }

    const params = new URLSearchParams(window.location.search)
    const connectUrl = params.get('connect')

    if (connectUrl) {
      const parsed = parseWsUrl(connectUrl)
      if (parsed) {
        setAppState('connecting')
        const client = connect(parsed.host, parsed.port, undefined, parsed.protocol)

        const cleanup = client.onConnectionStateChanged((state) => {
          if (state.status === 'connected') {
            cleanup()
            setupElectronApi(client)

            void (async () => {
              const workspaceId = await finalizeConnectedWorkspace(parsed.host, parsed.port, parsed.protocol)
              setWorkspaceId(workspaceId)

              const url = new URL(window.location.href)
              url.searchParams.delete('connect')
              window.history.replaceState({}, '', url.toString())
              setAppState('connected')
            })()
          } else if (state.status === 'failed' && state.attempt >= 3) {
            cleanup()
            setAppState('pairing')
          }
        })

        return cleanup
      }
    }

    const stored = getStoredConnection()
    if (!stored) return

    setAppState('connecting')
    const client = connect(stored.host, stored.port, stored.workspaceId, stored.protocol)

    const cleanup = client.onConnectionStateChanged((state) => {
      if (state.status === 'connected') {
        cleanup()
        setupElectronApi(client)

        void (async () => {
          const workspaceId = await finalizeConnectedWorkspace(stored.host, stored.port, stored.protocol, stored.workspaceId)
          setWorkspaceId(workspaceId)
          setAppState('connected')
        })()
      } else if (state.status === 'failed' && state.attempt >= 3) {
        cleanup()
        setAppState('pairing')
      }
    })

    return cleanup
  }, [])

  const handleConnected = useCallback((wsId?: string) => {
    setWorkspaceId(wsId)
    setAppState('connected')
  }, [])

  // Watch for "kicked by host" (close code 4008) and redirect to pairing
  useEffect(() => {
    if (appState !== 'connected') return

    const rpcClient = getClient()
    if (!rpcClient) return

    return rpcClient.onConnectionStateChanged((state) => {
      if (state.lastClose?.code === 4008) {
        disconnect()
        clearStoredConnection()
        setAppState('pairing')
      }
    })
  }, [appState])

  const handleRescan = useCallback(() => {
    setAppState('pairing')
  }, [])

  if (appState === 'pairing') {
    return <main className="absolute inset-0"><PairingScreen onConnected={handleConnected} /></main>
  }

  if (appState === 'connecting') {
    return (
      <main className="absolute inset-0 flex items-center justify-center bg-[var(--background)]">
        <div className="text-center space-y-3">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[var(--muted-foreground)]" />
          <p className="text-sm text-[var(--muted-foreground)]">Connecting to desktop...</p>
        </div>
      </main>
    )
  }

  return (
    <RelayAuthGate>
      <ConnectedApp onRescan={handleRescan} />
    </RelayAuthGate>
  )
}

// ─── Relay auth gate ───────────────────────────────────────────────────────────
// Share Online and knowledge writes run on the connected server with that
// server's Relay session. If it has none (or it was revoked), verify here.

function RelayAuthGate({ children }: { children: React.ReactNode }) {
  const [needsVerification, setNeedsVerification] = useState<boolean | null>(null)
  const [verifiedEmail, setVerifiedEmail] = useState<string | undefined>()
  // Apply only the newest probe's result (mount vs. pushed refreshes can overlap).
  const versionRef = useRef(0)

  const refresh = useCallback(() => {
    const version = ++versionRef.current
    window.electronAPI.getSetupNeeds().then((needs: SetupNeeds) => {
      if (version !== versionRef.current) return
      setVerifiedEmail(needs.verifiedEmail ?? undefined)
      setNeedsVerification(!needs.emailVerified)
    }).catch((err: unknown) => {
      if (version !== versionRef.current) return
      // Don't lock the user out over a failed probe; writes surface their own error.
      console.error('Failed to check Relay auth state:', err)
      setNeedsVerification(false)
    })
  }, [])

  useEffect(() => {
    refresh()
    const cleanup = window.electronAPI.onAuthStateChanged(refresh)
    return () => { cleanup() }
  }, [refresh])

  if (needsVerification === null) {
    return (
      <main className="absolute inset-0 flex items-center justify-center bg-[var(--background)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--muted-foreground)]" />
      </main>
    )
  }

  if (needsVerification) {
    return (
      <main className="absolute inset-0 flex items-center justify-center bg-[var(--background)]">
        <EmailVerificationStep initialEmail={verifiedEmail} onVerified={refresh} />
      </main>
    )
  }

  return <>{children}</>
}

// ─── Connected App (renders after window.electronAPI is set up) ────────────────

function ConnectedApp({ onRescan }: { onRescan: () => void }) {
  // Virtual keyboard layout adjustment
  useVirtualKeyboard()

  // Per-session Jotai atom setters
  const initializeSessions = useSetAtom(initializeSessionsAtom)
  const addSession = useSetAtom(addSessionAtom)
  const removeSession = useSetAtom(removeSessionAtom)
  const updateSessionDirect = useSetAtom(updateSessionAtom)
  const store = useStore()

  const updateSessionById = useCallback((
    sessionId: string,
    updates: Partial<Session> | ((session: Session) => Partial<Session>)
  ) => {
    updateSessionDirect(sessionId, (prev) => {
      if (!prev) return prev
      const partialUpdates = typeof updates === 'function' ? updates(prev) : updates
      return { ...prev, ...partialUpdates }
    })
  }, [updateSessionDirect])

  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [windowWorkspaceId, setWindowWorkspaceId] = useAtom(windowWorkspaceIdAtom)

  const windowWorkspaceSlug = useMemo(() => {
    if (!windowWorkspaceId) return null
    const workspace = workspaces.find(w => w.id === windowWorkspaceId)
    if (!workspace?.rootPath) return windowWorkspaceId
    return extractWorkspaceSlugFromPath(workspace.rootPath, windowWorkspaceId)
  }, [windowWorkspaceId, workspaces])

  const [llmConnections, setLlmConnections] = useState<LlmConnectionWithStatus[]>([])
  const [workspaceDefaultLlmConnection, setWorkspaceDefaultLlmConnection] = useState<string | undefined>()
  const [defaultLlmConnectionSlug, setDefaultLlmConnectionSlug] = useState<string | undefined>()

  const [pendingPermissions, setPendingPermissions] = useState<Map<string, PermissionRequest[]>>(new Map())
  const [pendingCredentials, setPendingCredentials] = useState<Map<string, CredentialRequest[]>>(new Map())
  // AskUserQuestion requests per session (queue to handle multiple concurrent requests)
  const [pendingAskUserQuestions, setPendingAskUserQuestions] = useState<Map<string, AskUserQuestionRequest[]>>(new Map())
  const sessionDraftsRef = useRef<Map<string, string>>(new Map())
  const [sessionOptions, setSessionOptions] = useState<Map<string, SessionOptions>>(new Map())

  const [appTheme, setAppTheme] = useState<ThemeOverrides | null>(null)
  const [sessionsLoaded, setSessionsLoaded] = useState(false)

  const sources = useAtomValue(sourcesAtom)
  const skills = useAtomValue(skillsAtom)

  const { shikiTheme, isDark } = useTheme({ appTheme })

  const sessionOptionsRef = useRef(sessionOptions)
  useEffect(() => { sessionOptionsRef.current = sessionOptions }, [sessionOptions])

  // Event processor
  const { processAgentEvent } = useEventProcessor()

  const DRAFT_SAVE_DEBOUNCE_MS = 500

  const resolveDefaultConnectionSlug = useCallback((connections: LlmConnectionWithStatus[]) => {
    return connections.find(c => c.isDefault)?.slug ?? connections[0]?.slug
  }, [])

  const refreshLlmConnections = useCallback(async () => {
    const connections = await window.electronAPI.listLlmConnectionsWithStatus()
    setLlmConnections(connections)
    setDefaultLlmConnectionSlug(resolveDefaultConnectionSlug(connections))
    if (windowWorkspaceId) {
      const settings = await window.electronAPI.getWorkspaceSettings(windowWorkspaceId)
      setWorkspaceDefaultLlmConnection(settings?.defaultLlmConnection)
    }
  }, [resolveDefaultConnectionSlug, windowWorkspaceId])

  // Permission mode helpers
  const applyPermissionModeState = useCallback((sessionId: string, state: PermissionModeState, _source: 'event' | 'reconcile') => {
    setSessionOptions(prev => {
      const next = new Map(prev)
      const current = next.get(sessionId) ?? defaultSessionOptions
      const currentVersion = current.permissionModeVersion ?? -1
      if (state.modeVersion < currentVersion) return prev
      next.set(sessionId, {
        ...current,
        permissionMode: state.permissionMode,
        permissionModeVersion: state.modeVersion,
      })
      return next
    })
  }, [])

  const reconcilePermissionModeState = useCallback(async (sessionId: string) => {
    try {
      const state = await window.electronAPI.getSessionPermissionModeState(sessionId)
      if (!state) return
      applyPermissionModeState(sessionId, state, 'reconcile')
    } catch {
      // Ignore errors — session may have been deleted
    }
  }, [applyPermissionModeState])

  // Session selection
  const [, setSession] = useSession()

  // Populate session options for newly created/discovered sessions
  const populateSessionOptions = useCallback((session: Session) => {
    const hasNonDefaultMode = session.permissionMode && session.permissionMode !== 'allow-all'
    const hasNonDefaultThinking = session.thinkingLevel && session.thinkingLevel !== DEFAULT_THINKING_LEVEL
    if (hasNonDefaultMode || hasNonDefaultThinking) {
      setSessionOptions(prev => {
        const next = new Map(prev)
        next.set(session.id, {
          permissionMode: session.permissionMode ?? 'allow-all',
          thinkingLevel: session.thinkingLevel ?? DEFAULT_THINKING_LEVEL,
        })
        return next
      })
    }
  }, [])

  // ─── Data Loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    window.electronAPI.getWorkspaces().then((ws: Workspace[]) => {
      setWorkspaces(ws)
      if (ws.length > 0) {
        setWindowWorkspaceId(ws[0].id)
        // Update the transport client's workspace ID so future reconnections
        // include the correct workspace in the handshake. Without this, reconnects
        // would register with workspaceId=null and miss all workspace-scoped events.
        setClientWorkspaceId(ws[0].id)
        // Register workspace with the server so push events are routed to this client.
        // The remote client connects without a workspaceId in the WS handshake,
        // so we must call switchWorkspace to update the server's client-workspace mapping.
        window.electronAPI.switchWorkspace(ws[0].id).catch(() => {})
      }
    }).catch(() => {})

    window.electronAPI.getSessions().then(async (loadedSessions: Session[]) => {
      initializeSessions(loadedSessions)
      const optionsMap = new Map<string, SessionOptions>()
      for (const s of loadedSessions) {
        const hasNonDefaultMode = s.permissionMode && s.permissionMode !== 'allow-all'
        const hasNonDefaultThinking = s.thinkingLevel && s.thinkingLevel !== DEFAULT_THINKING_LEVEL
        if (hasNonDefaultMode || hasNonDefaultThinking) {
          optionsMap.set(s.id, {
            permissionMode: s.permissionMode ?? 'allow-all',
            thinkingLevel: s.thinkingLevel ?? DEFAULT_THINKING_LEVEL,
          })
        }
      }
      setSessionOptions(optionsMap)

      await Promise.allSettled(
        loadedSessions.map((s: Session) => reconcilePermissionModeState(s.id))
      )

      setSessionsLoaded(true)
    }).catch(() => {})

    window.electronAPI.listLlmConnectionsWithStatus().then((connections: LlmConnectionWithStatus[]) => {
      setLlmConnections(connections)
      setDefaultLlmConnectionSlug(resolveDefaultConnectionSlug(connections))
    }).catch(() => {})

    window.electronAPI.getAllDrafts().then((drafts: Record<string, string>) => {
      if (Object.keys(drafts).length > 0) {
        sessionDraftsRef.current = new Map(Object.entries(drafts))
      }
    }).catch(() => {})

    window.electronAPI.getAppTheme().then(setAppTheme).catch(() => {})
  }, [initializeSessions, resolveDefaultConnectionSlug, reconcilePermissionModeState])

  // Subscribe to theme changes
  useEffect(() => {
    const cleanup = window.electronAPI.onAppThemeChange((theme: ThemeOverrides) => {
      setAppTheme(theme)
    })
    return () => { cleanup() }
  }, [])

  // Subscribe to LLM connection changes
  useEffect(() => {
    const cleanup = window.electronAPI.onLlmConnectionsChanged(() => {
      refreshLlmConnections()
    })
    return () => { cleanup() }
  }, [refreshLlmConnections])

  // Reconcile state after WebSocket reconnect (events may have been lost while disconnected)
  useEffect(() => {
    const cleanup = window.electronAPI.onTransportConnectionStateChanged?.((state: { status: string; isReconnect?: boolean }) => {
      if (state.status === 'connected' && state.isReconnect) {
        // Reconnected — re-register workspace so push events are routed correctly,
        // then refetch mutable state to reconcile any events missed while disconnected
        if (windowWorkspaceId) {
          window.electronAPI.switchWorkspace(windowWorkspaceId).catch(() => {})
        }
        window.electronAPI.getSessions().then(initializeSessions).catch(() => {})
        refreshLlmConnections()
        window.electronAPI.getAppTheme().then(setAppTheme).catch(() => {})
      }
    })
    return () => { cleanup?.() }
  }, [initializeSessions, refreshLlmConnections, windowWorkspaceId])

  useEffect(() => {
    if (windowWorkspaceId) {
      refreshLlmConnections()
    }
  }, [windowWorkspaceId, refreshLlmConnections])

  // ─── Session Event Listener ────────────────────────────────────────────────

  useEffect(() => {
    const handoffEventTypes = new Set([
      'complete', 'error', 'interrupted', 'typed_error',
      'session_status_changed', 'session_flagged', 'session_unflagged',
      'name_changed', 'labels_changed', 'title_generated', 'async_operation',
    ])

    const handleEffects = (effects: Effect[], sessionId: string, eventType: string) => {
      for (const effect of effects) {
        switch (effect.type) {
          case 'permission_request': {
            setPendingPermissions(prev => {
              const next = new Map(prev)
              const existing = next.get(sessionId) || []
              next.set(sessionId, [...existing, effect.request])
              return next
            })
            break
          }
          case 'permission_mode_changed': {
            if (typeof effect.modeVersion === 'number' && effect.changedAt && effect.changedBy) {
              applyPermissionModeState(effect.sessionId, {
                permissionMode: effect.permissionMode,
                modeVersion: effect.modeVersion,
                changedAt: effect.changedAt,
                changedBy: effect.changedBy,
              }, 'event')
            } else {
              setSessionOptions(prev => {
                const next = new Map(prev)
                const current = next.get(effect.sessionId) ?? defaultSessionOptions
                next.set(effect.sessionId, { ...current, permissionMode: effect.permissionMode })
                return next
              })
              void reconcilePermissionModeState(effect.sessionId)
            }
            break
          }
          case 'credential_request': {
            setPendingCredentials(prev => {
              const next = new Map(prev)
              const existing = next.get(sessionId) || []
              next.set(sessionId, [...existing, effect.request])
              return next
            })
            break
          }
          case 'ask_user_question_request': {
            setPendingAskUserQuestions(prev => {
              const next = new Map(prev)
              const existing = next.get(sessionId) || []
              next.set(sessionId, [...existing, effect.request])
              return next
            })
            break
          }
          case 'auto_retry': {
            const messageWithSuffix = `${effect.originalMessage}\n\n[${effect.sourceSlug} activated]`
            setTimeout(() => {
              void window.electronAPI.sendMessage(effect.sessionId, messageWithSuffix).catch(() => {})
            }, 100)
            break
          }
          case 'restore_input': {
            const existingDraft = sessionDraftsRef.current.get(sessionId) ?? ''
            const restored = existingDraft
              ? `${existingDraft}\n\n${effect.text}`
              : effect.text
            handleInputChange(sessionId, restored)
            window.dispatchEvent(new CustomEvent('craft:restore-input', {
              detail: { sessionId, text: restored },
            }))
            break
          }
        }
      }

      // Clear pending structured inputs on terminal events
      const isTerminalEvent = eventType === 'complete' || eventType === 'error' || eventType === 'interrupted' || eventType === 'typed_error'
      if (isTerminalEvent) {
        setPendingPermissions(prev => {
          if (prev.has(sessionId)) {
            const next = new Map(prev)
            next.delete(sessionId)
            return next
          }
          return prev
        })
        setPendingCredentials(prev => {
          if (prev.has(sessionId)) {
            const next = new Map(prev)
            next.delete(sessionId)
            return next
          }
          return prev
        })
        setPendingAskUserQuestions(prev => {
          if (prev.has(sessionId)) {
            const next = new Map(prev)
            next.delete(sessionId)
            return next
          }
          return prev
        })
      }
    }

    const cleanup = window.electronAPI.onSessionEvent((event: SessionEvent) => {
      if (!('sessionId' in event)) return

      const sessionId = event.sessionId
      const workspaceId = windowWorkspaceId ?? ''

      if (event.type === 'session_created') {
        window.electronAPI.getSessionMessages(sessionId)
          .then((createdSession: Session | null) => {
            if (createdSession) {
              const existingMeta = store.get(sessionMetaMapAtom).has(sessionId)
              if (existingMeta) {
                updateSessionDirect(sessionId, () => createdSession)
                const metaMap = store.get(sessionMetaMapAtom)
                const nextMetaMap = new Map(metaMap)
                nextMetaMap.set(sessionId, extractSessionMeta(createdSession))
                store.set(sessionMetaMapAtom, nextMetaMap)
              } else {
                addSession(createdSession)
              }
              populateSessionOptions(createdSession)
            } else {
              window.electronAPI.getSessions().then(initializeSessions).catch(() => {})
            }
          })
          .catch((error: unknown) => console.error('Failed to handle session_created:', error))
        return
      }

      if (event.type === 'session_deleted') {
        removeSession(sessionId)
        return
      }

      const agentEvent = event as unknown as AgentEvent

      if (event.type === 'info' && (event as any).statusType === 'compaction_complete') {
        window.dispatchEvent(new CustomEvent('craft:compaction-complete', {
          detail: { sessionId },
        }))
      }

      const atomSession = store.get(sessionAtomFamily(sessionId))
      const isStreaming = atomSession?.isProcessing === true
      const isHandoff = handoffEventTypes.has(event.type)

      if (isStreaming || isHandoff) {
        const currentSession = atomSession ?? null
        const { session: updatedSession, effects } = processAgentEvent(
          agentEvent, currentSession, workspaceId
        )
        updateSessionDirect(sessionId, () => updatedSession)
        handleEffects(effects, sessionId, event.type)
        handleBackgroundTaskEvent(store, sessionId, event, agentEvent)

        if (isHandoff) {
          const metaMap = store.get(sessionMetaMapAtom)
          const newMetaMap = new Map(metaMap)
          newMetaMap.set(sessionId, extractSessionMeta(updatedSession))
          store.set(sessionMetaMapAtom, newMetaMap)
        }
        return
      }

      const currentSession = store.get(sessionAtomFamily(sessionId))
      const { session: updatedSession, effects } = processAgentEvent(
        agentEvent, currentSession, workspaceId
      )
      handleEffects(effects, sessionId, event.type)
      handleBackgroundTaskEvent(store, sessionId, event, agentEvent)
      updateSessionDirect(sessionId, () => updatedSession)

      const metaMap = store.get(sessionMetaMapAtom)
      const newMetaMap = new Map(metaMap)
      newMetaMap.set(sessionId, extractSessionMeta(updatedSession))
      store.set(sessionMetaMapAtom, newMetaMap)
    })

    return cleanup
  }, [
    processAgentEvent, windowWorkspaceId, store,
    updateSessionDirect, initializeSessions, addSession, removeSession,
    applyPermissionModeState, reconcilePermissionModeState,
  ])

  // ─── Session Callbacks ─────────────────────────────────────────────────────

  const handleCreateSession = useCallback(async (workspaceId: string, options?: import('@/shared/types').CreateSessionOptions): Promise<Session> => {
    const session = await window.electronAPI.createSession(workspaceId, options)
    addSession(session)
    populateSessionOptions(session)
    return session
  }, [addSession, populateSessionOptions])

  const handleDeleteSession = useCallback(async (sessionId: string, skipConfirmation = false): Promise<boolean> => {
    if (!skipConfirmation) {
      const metaMap = store.get(sessionMetaMapAtom)
      const meta = metaMap.get(sessionId)
      const isEmpty = !meta || (!meta.lastFinalMessageId && !meta.name)
      if (!isEmpty) {
        const confirmed = await window.electronAPI.showDeleteSessionConfirmation(meta?.name || 'Untitled')
        if (!confirmed) return false
      }
    }
    await window.electronAPI.deleteSession(sessionId)
    removeSession(sessionId)
    return true
  }, [store, removeSession])

  const handleAutoDeleteEmptySession = useCallback((sessionId: string) => {
    window.electronAPI.deleteSession(sessionId).catch(() => {})
    removeSession(sessionId)
  }, [removeSession])

  const handleFlagSession = useCallback((sessionId: string) => {
    updateSessionById(sessionId, { isFlagged: true })
    window.electronAPI.sessionCommand(sessionId, { type: 'flag' }).catch(() => {})
  }, [updateSessionById])

  const handleUnflagSession = useCallback((sessionId: string) => {
    updateSessionById(sessionId, { isFlagged: false })
    window.electronAPI.sessionCommand(sessionId, { type: 'unflag' }).catch(() => {})
  }, [updateSessionById])

  const handleArchiveSession = useCallback((sessionId: string) => {
    updateSessionById(sessionId, { isArchived: true, archivedAt: Date.now() })
    window.electronAPI.sessionCommand(sessionId, { type: 'archive' }).catch(() => {})
  }, [updateSessionById])

  const handleUnarchiveSession = useCallback((sessionId: string) => {
    updateSessionById(sessionId, { isArchived: false, archivedAt: undefined })
    window.electronAPI.sessionCommand(sessionId, { type: 'unarchive' }).catch(() => {})
  }, [updateSessionById])

  const handleSetActiveViewingSession = useCallback((sessionId: string) => {
    updateSessionById(sessionId, { hasUnread: false })
    window.electronAPI.sessionCommand(sessionId, { type: 'setActiveViewing', workspaceId: windowWorkspaceId ?? '' }).catch(() => {})
  }, [updateSessionById, windowWorkspaceId])

  const handleMarkSessionRead = useCallback((sessionId: string) => {
    updateSessionById(sessionId, (s) => {
      const lastFinalId = s.messages.findLast(
        m => (m.role === 'assistant' || m.role === 'plan') && !m.isIntermediate
      )?.id
      return {
        hasUnread: false,
        ...(lastFinalId ? { lastReadMessageId: lastFinalId } : {}),
      }
    })
    window.electronAPI.sessionCommand(sessionId, { type: 'markRead' }).catch(() => {})
  }, [updateSessionById])

  const handleMarkSessionUnread = useCallback((sessionId: string) => {
    updateSessionById(sessionId, { hasUnread: true, lastReadMessageId: undefined })
    window.electronAPI.sessionCommand(sessionId, { type: 'markUnread' }).catch(() => {})
  }, [updateSessionById])

  const handleSessionStatusChange = useCallback((sessionId: string, state: SessionStatus) => {
    updateSessionById(sessionId, { sessionStatus: state })
    window.electronAPI.sessionCommand(sessionId, { type: 'setSessionStatus', state }).catch(() => {})
  }, [updateSessionById])

  const handleRenameSession = useCallback((sessionId: string, name: string) => {
    updateSessionById(sessionId, { name })
    window.electronAPI.sessionCommand(sessionId, { type: 'rename', name }).catch(() => {})
  }, [updateSessionById])

  const handleSendMessage = useCallback(async (
    sessionId: string,
    message: string,
    attachments?: FileAttachment[],
    skillSlugs?: string[],
    externalBadges?: ContentBadge[],
    delivery?: 'steer' | 'interrupt',
  ) => {
    try {
      let storedAttachments: StoredAttachment[] | undefined
      let processedAttachments: FileAttachment[] | undefined

      if (attachments?.length) {
        const storeResults = await Promise.allSettled(
          attachments.map(a => {
            const canPathCopy = a.type !== 'image'
              && isAbsoluteFilesystemPath(a.path)
              && typeof window.electronAPI.storeAttachmentFromPath === 'function'
            if (canPathCopy) {
              return window.electronAPI.storeAttachmentFromPath(sessionId, a.path, a.name)
            }
            return window.electronAPI.storeAttachment(sessionId, a)
          })
        )
        storedAttachments = []
        const successfulAttachments: FileAttachment[] = []
        storeResults.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            storedAttachments!.push(result.value)
            successfulAttachments.push(attachments[i])
          } else {
            console.warn(`Failed to store attachment "${attachments[i].name}":`, result.reason)
          }
        })

        const failedCount = storeResults.filter(r => r.status === 'rejected').length
        if (failedCount > 0) {
          const failedNames = attachments
            .filter((_, i) => storeResults[i].status === 'rejected')
            .map(a => a.name)
            .join(', ')
          updateSessionById(sessionId, (s) => ({
            messages: [...s.messages, {
              id: generateMessageId(),
              role: 'error' as const,
              content: `⚠️ ${failedCount} attachment(s) could not be stored and will not be sent: ${failedNames}`,
              timestamp: Date.now(),
            }],
          }))
          toast.error(`${failedCount} attachment(s) could not be stored: ${failedNames}`)
          const allFailed = failedCount === attachments.length
          if (allFailed && !message.trim()) {
            throw new Error('ATTACHMENTS_FAILED')
          }
          if (allFailed) {
            storedAttachments = undefined
            processedAttachments = undefined
          }
        }

        if (successfulAttachments.length > 0) {
          processedAttachments = successfulAttachments.map((att, i) => {
            const stored = storedAttachments?.[i]
            if (!stored) return att
            return toAgentFileAttachment(att, stored)
          })
        }
      }

      const mentionBadges: ContentBadge[] = windowWorkspaceSlug
        ? extractBadges(message, skills, sources, windowWorkspaceSlug)
        : []
      const badges: ContentBadge[] = [...(externalBadges || []), ...mentionBadges]

      const commandMatch = message.match(/^\/([a-z]+)(\s|$)/i)
      if (commandMatch && commandMatch[1].toLowerCase() === 'compact') {
        const commandText = commandMatch[0].trimEnd()
        badges.unshift({
          type: 'command',
          label: 'Compact',
          rawText: commandText,
          start: 0,
          end: commandText.length,
        })
      }

      const planExecuteMatch = message.match(/^(Read the plan at )(.+?)( and execute it\.?)$/i)
      if (planExecuteMatch) {
        const prefix = planExecuteMatch[1]
        const filePath = planExecuteMatch[2]
        const fileName = filePath.split('/').pop() || 'plan.md'
        badges.push({
          type: 'file',
          label: fileName,
          rawText: filePath,
          filePath,
          start: prefix.length,
          end: prefix.length + filePath.length,
        })
      }

      const userMessage: Message = {
        id: generateMessageId(),
        role: 'user',
        content: message,
        timestamp: Date.now(),
        attachments: storedAttachments,
        badges: badges.length > 0 ? badges : undefined,
        isPending: true,
      }

      updateSessionById(sessionId, (s) => ({
        messages: [...s.messages, userMessage],
        isProcessing: true,
        lastMessageAt: Date.now(),
      }))

      await window.electronAPI.sendMessage(sessionId, message, processedAttachments, storedAttachments, {
        skillSlugs,
        badges: badges.length > 0 ? badges : undefined,
        optimisticMessageId: userMessage.id,
        delivery,
      })
    } catch (error) {
      if (error instanceof Error && error.message === 'ATTACHMENTS_FAILED') {
        throw error
      }
      console.error('Failed to send message:', error)
      updateSessionById(sessionId, (s) => ({
        isProcessing: false,
        messages: [
          ...s.messages,
          {
            id: generateMessageId(),
            role: 'error' as const,
            content: `Failed to send message: ${error instanceof Error ? error.message : 'Unknown error'}`,
            timestamp: Date.now(),
          },
        ],
      }))
    }
  }, [updateSessionById, skills, sources, windowWorkspaceSlug])

  const handleSessionOptionsChange = useCallback((sessionId: string, updates: SessionOptionUpdates) => {
    setSessionOptions(prev => {
      const next = new Map(prev)
      const current = next.get(sessionId) ?? defaultSessionOptions
      next.set(sessionId, mergeSessionOptions(current, updates))
      return next
    })
    if (updates.permissionMode !== undefined) {
      window.electronAPI.sessionCommand(sessionId, { type: 'setPermissionMode', mode: updates.permissionMode }).catch(() => {})
    }
    if (updates.thinkingLevel !== undefined) {
      window.electronAPI.sessionCommand(sessionId, { type: 'setThinkingLevel', level: updates.thinkingLevel }).catch(() => {})
    }
  }, [])

  // Draft input handling with debounced persistence
  const draftSaveTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const timeoutMap = draftSaveTimeoutRef.current
    return () => {
      timeoutMap.forEach(clearTimeout)
      timeoutMap.clear()
    }
  }, [])

  const getDraft = useCallback((sessionId: string): string => {
    return sessionDraftsRef.current.get(sessionId) ?? ''
  }, [])

  const handleInputChange = useCallback((sessionId: string, value: string) => {
    if (value) {
      sessionDraftsRef.current.set(sessionId, value)
    } else {
      sessionDraftsRef.current.delete(sessionId)
    }
    const existingTimeout = draftSaveTimeoutRef.current.get(sessionId)
    if (existingTimeout) clearTimeout(existingTimeout)
    const timeout = setTimeout(() => {
      window.electronAPI.setDraft(sessionId, value).catch(() => {})
      draftSaveTimeoutRef.current.delete(sessionId)
    }, DRAFT_SAVE_DEBOUNCE_MS)
    draftSaveTimeoutRef.current.set(sessionId, timeout)
  }, [])

  const openNewChat = useCallback(async (params: NewChatActionParams = {}) => {
    if (!windowWorkspaceId) return
    const session = await handleCreateSession(windowWorkspaceId)
    if (params.name) {
      await window.electronAPI.sessionCommand(session.id, { type: 'rename', name: params.name })
    }
    navigate(routes.view.allSessions(session.id))
    if (params.input) {
      setTimeout(() => handleInputChange(session.id, params.input!), 100)
    }
  }, [windowWorkspaceId, handleCreateSession, handleInputChange])

  const handleRespondToPermission = useCallback(async (
    sessionId: string,
    requestId: string,
    allowed: boolean,
    alwaysAllow: boolean,
    options?: import('@/shared/types').PermissionResponseOptions,
  ) => {
    const success = await window.electronAPI.respondToPermission(sessionId, requestId, allowed, alwaysAllow, options)
    setPendingPermissions(prev => {
      const next = new Map(prev)
      const queue = next.get(sessionId) || []
      const remaining = queue.slice(1)
      if (remaining.length === 0) next.delete(sessionId)
      else next.set(sessionId, remaining)
      return next
    })
  }, [])

  const handleRespondToCredential = useCallback(async (sessionId: string, requestId: string, response: CredentialResponse) => {
    await window.electronAPI.respondToCredential(sessionId, requestId, response)
    setPendingCredentials(prev => {
      const next = new Map(prev)
      const queue = next.get(sessionId) || []
      const remaining = queue.slice(1)
      if (remaining.length === 0) next.delete(sessionId)
      else next.set(sessionId, remaining)
      return next
    })
  }, [])

  const handleRespondToAskUserQuestion = useCallback(async (requestId: string, answers: AskUserQuestionAnswer[]) => {
    try {
      await window.electronAPI.respondToAskUserQuestion(requestId, answers)
    } catch {
      return // Don't dequeue if the RPC failed — backend is still waiting
    }

    // Remove only the first ask_user_question from each queue that matches this requestId
    setPendingAskUserQuestions(prev => {
      const next = new Map(prev)
      for (const [sessionId, queue] of next.entries()) {
        const idx = queue.findIndex(q => q.requestId === requestId)
        if (idx !== -1) {
          const remaining = [...queue.slice(0, idx), ...queue.slice(idx + 1)]
          if (remaining.length === 0) {
            next.delete(sessionId)
          } else {
            next.set(sessionId, remaining)
          }
          break
        }
      }
      return next
    })
  }, [])

  // Workspace handling
  const handleSelectWorkspace = useCallback(async (workspaceId: string) => {
    if (workspaceId === windowWorkspaceId) return
    try {
      await window.electronAPI.switchWorkspace(workspaceId)
    } catch {
      return
    }
    setWindowWorkspaceId(workspaceId)
    setSession({ selected: null })
    setPendingPermissions(new Map())
    setPendingCredentials(new Map())
    setPendingAskUserQuestions(new Map())
    setSessionOptions(new Map())
    sessionDraftsRef.current.clear()
    store.set(sourcesAtom, [])
    store.set(skillsAtom, [])
    store.set(sessionMetaMapAtom, new Map())
    store.set(sessionIdsAtom, [])
  }, [windowWorkspaceId, setSession, store])

  const handleSwitchWorkspaceBySlug = useCallback((slug: string) => {
    const target = workspaces.find(w => {
      const wsSlug = extractWorkspaceSlugFromPath(w.rootPath, w.id)
      return wsSlug === slug
    })
    if (target) handleSelectWorkspace(target.id)
  }, [workspaces, handleSelectWorkspace])

  const handleRefreshWorkspaces = useCallback(() => {
    window.electronAPI.getWorkspaces().then(setWorkspaces).catch(() => {})
  }, [])

  // Link interceptor for file previews
  const linkInterceptor = useLinkInterceptor({
    openFileExternal: async () => { /* no-op on remote client */ },
    openUrl: async (url) => { window.open(url, '_blank', 'noopener,noreferrer') },
    showInFolder: async () => { /* no-op on remote client */ },
    readFile: (path) => window.electronAPI.readFile(path),
    readFileDataUrl: (path) => window.electronAPI.readFileDataUrl(path),
    readFileBinary: (path) => window.electronAPI.readFileBinary(path),
  })

  const transportConnectionState = useTransportConnectionState()
  const showTransportConnectionBanner = shouldShowTransportConnectionBanner(transportConnectionState)

  const handleReconnectTransport = useCallback(() => {
    void window.electronAPI.reconnectTransport().catch((error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast.error('Reconnect failed', { description: message })
    })
  }, [])

  const handleOpenFile = linkInterceptor.handleOpenFile
  const handleOpenUrl = linkInterceptor.handleOpenUrl

  const handleOpenSettings = useCallback(() => { navigate(routes.view.settings()) }, [])
  const handleOpenKeyboardShortcuts = useCallback(() => { navigate(routes.view.settings('shortcuts')) }, [])
  const handleOpenStoredUserPreferences = useCallback(() => { navigate(routes.view.settings('preferences')) }, [])
  const handleReset = useCallback(() => { /* no-op on remote client — reset on desktop */ }, [])

  // ─── Context Value ─────────────────────────────────────────────────────────

  const appShellContextValue = useMemo<AppShellContextType>(() => ({
    workspaces,
    activeWorkspaceId: windowWorkspaceId,
    activeWorkspaceSlug: windowWorkspaceSlug,
    llmConnections,
    workspaceDefaultLlmConnection,
    refreshLlmConnections,
    pendingPermissions,
    pendingCredentials,
    pendingAskUserQuestions,
    getDraft,
    sessionOptions,
    onCreateSession: handleCreateSession,
    onSendMessage: handleSendMessage,
    onRenameSession: handleRenameSession,
    onFlagSession: handleFlagSession,
    onUnflagSession: handleUnflagSession,
    onArchiveSession: handleArchiveSession,
    onUnarchiveSession: handleUnarchiveSession,
    onMarkSessionRead: handleMarkSessionRead,
    onMarkSessionUnread: handleMarkSessionUnread,
    onSetActiveViewingSession: handleSetActiveViewingSession,
    onSessionStatusChange: handleSessionStatusChange,
    onDeleteSession: handleDeleteSession,
    onRespondToPermission: handleRespondToPermission,
    onRespondToCredential: handleRespondToCredential,
    onRespondToAskUserQuestion: handleRespondToAskUserQuestion,
    onOpenFile: handleOpenFile,
    onOpenUrl: handleOpenUrl,
    onSelectWorkspace: handleSelectWorkspace,
    onRefreshWorkspaces: handleRefreshWorkspaces,
    onOpenSettings: handleOpenSettings,
    onOpenKeyboardShortcuts: handleOpenKeyboardShortcuts,
    onOpenStoredUserPreferences: handleOpenStoredUserPreferences,
    onReset: handleReset,
    onSessionOptionsChange: handleSessionOptionsChange,
    onInputChange: handleInputChange,
    openNewChat,
  }), [
    workspaces, windowWorkspaceId, windowWorkspaceSlug,
    llmConnections, workspaceDefaultLlmConnection, refreshLlmConnections,
    pendingPermissions, pendingCredentials, pendingAskUserQuestions, getDraft, sessionOptions,
    handleCreateSession, handleSendMessage, handleRenameSession,
    handleFlagSession, handleUnflagSession, handleArchiveSession, handleUnarchiveSession,
    handleMarkSessionRead, handleMarkSessionUnread, handleSetActiveViewingSession,
    handleSessionStatusChange, handleDeleteSession,
    handleRespondToPermission, handleRespondToCredential, handleRespondToAskUserQuestion,
    handleOpenFile, handleOpenUrl,
    handleSelectWorkspace, handleRefreshWorkspaces,
    handleOpenSettings, handleOpenKeyboardShortcuts, handleOpenStoredUserPreferences,
    handleReset, handleSessionOptionsChange, handleInputChange, openNewChat,
  ])

  const platformActions = useMemo(() => ({
    onOpenFile: handleOpenFile,
    onOpenUrl: handleOpenUrl,
    onOpenFileExternal: linkInterceptor.openFileExternal,
    onReadFile: (path: string) => window.electronAPI.readFile(path),
    onReadFileDataUrl: (path: string) => window.electronAPI.readFileDataUrl(path),
    onReadFileBinary: (path: string) => window.electronAPI.readFileBinary(path),
    onRevealInFinder: () => { /* no-op on remote client */ },
    fileManagerName: getFileManagerName(),
    onSetTrafficLightsVisible: () => { /* no-op on remote client */ },
  }), [handleOpenFile, handleOpenUrl, linkInterceptor.openFileExternal])

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <PlatformProvider actions={platformActions}>
    <ShikiThemeProvider shikiTheme={shikiTheme}>
      <ActionRegistryProvider>
      <FocusProvider>
        <DismissibleLayerProvider>
        <ModalProvider>
        <TooltipProvider delayDuration={0}>
        <NavigationProvider
          workspaceId={windowWorkspaceId}
          workspaceSlug={windowWorkspaceSlug}
          onSwitchWorkspaceBySlug={handleSwitchWorkspaceBySlug}
          onCreateSession={handleCreateSession}
          onInputChange={handleInputChange}
          getDraft={getDraft}
          onAutoDeleteEmptySession={handleAutoDeleteEmptySession}
          isReady={sessionsLoaded}
          isSessionsReady={sessionsLoaded}
        >
          <div
            className="absolute inset-0 flex flex-col overflow-hidden text-foreground"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--topbar-height))' }}
          >
            <ConnectionBanner onRescan={onRescan} />
            <OfflineBanner />
            {showTransportConnectionBanner && transportConnectionState && (
              <TransportConnectionBanner
                state={transportConnectionState}
                onRetry={handleReconnectTransport}
              />
            )}
            <main className="flex-1 min-h-0">
              <AppShell
                contextValue={appShellContextValue}
                defaultLayout={[20, 32, 48]}
              />
            </main>
          </div>

          {linkInterceptor.previewState && (
            <FilePreviewRenderer
              state={linkInterceptor.previewState}
              onClose={linkInterceptor.closePreview}
              loadDataUrl={linkInterceptor.readFileDataUrl}
              loadPdfData={linkInterceptor.readFileBinary}
              isDark={isDark}
              onRefresh={linkInterceptor.handleOpenFile}
            />
          )}
        </NavigationProvider>
        </TooltipProvider>
        </ModalProvider>
        </DismissibleLayerProvider>
      </FocusProvider>
      </ActionRegistryProvider>
    </ShikiThemeProvider>
    </PlatformProvider>
  )
}

// ─── File Preview Renderer ───────────────────────────────────────────────────

function FilePreviewRenderer({
  state,
  onClose,
  loadDataUrl,
  loadPdfData,
  isDark,
  onRefresh,
}: {
  state: FilePreviewState
  onClose: () => void
  loadDataUrl: (path: string) => Promise<string>
  loadPdfData: (path: string) => Promise<Uint8Array>
  isDark: boolean
  onRefresh?: (path: string) => void
}) {
  const theme: 'dark' | 'light' = isDark ? 'dark' : 'light'

  switch (state.type) {
    case 'image':
      return (
        <ImagePreviewOverlay isOpen onClose={onClose}
          filePath={state.filePath} loadDataUrl={loadDataUrl} theme={theme} />
      )
    case 'pdf':
      return (
        <PDFPreviewOverlay isOpen onClose={onClose}
          filePath={state.filePath} loadPdfData={loadPdfData} theme={theme} />
      )
    case 'docx':
      return (
        <DocxPreviewOverlay isOpen onClose={onClose}
          filePath={state.filePath} loadDocxData={loadPdfData} theme={theme} />
      )
    case 'code':
    case 'text':
      return (
        <CodePreviewOverlay isOpen onClose={onClose}
          filePath={state.filePath} content={state.content ?? ''}
          language={state.type === 'code' ? state.language : 'plaintext'}
          mode="read" theme={theme} error={state.error}
          headerActions={isHtmlFile(state.filePath)
            ? <OpenInBrowserButton filePath={state.filePath} content={state.content ?? ''} />
            : undefined} />
      )
    case 'markdown': {
      const isPlanFile =
        (state.filePath.includes('/plans/') || state.filePath.startsWith('plans/')) &&
        state.filePath.endsWith('.md')
      return (
        <EditableMarkdownOverlay isOpen onClose={onClose}
          content={state.content ?? ''} filePath={state.filePath}
          variant={isPlanFile ? 'plan' : 'response'}
          onRefresh={onRefresh ? () => onRefresh(state.filePath) : undefined} />
      )
    }
    case 'json': {
      let parsedData: unknown = null
      try { if (state.content) parsedData = JSON.parse(state.content) }
      catch {
        return (
          <CodePreviewOverlay isOpen onClose={onClose}
            filePath={state.filePath} content={state.content ?? ''}
            language="json" mode="read" theme={theme} error={state.error} />
        )
      }
      return (
        <JSONPreviewOverlay isOpen onClose={onClose}
          filePath={state.filePath} title={state.filePath.split('/').pop() ?? 'JSON'}
          data={parsedData} theme={theme} error={state.error} />
      )
    }
    default:
      return null
  }
}

/** Returns true if the path looks like an HTML file (.html / .htm, case-insensitive). */
function isHtmlFile(path: string): boolean {
  return /\.html?$/i.test(path)
}

/**
 * Header action button shown in CodePreviewOverlay when viewing an HTML file.
 * On the remote client we have no access to the desktop's filesystem, so we
 * render the loaded HTML into a Blob URL and open it in a new browser tab.
 */
function OpenInBrowserButton({ filePath, content }: { filePath: string; content: string }) {
  const handleClick = useCallback(() => {
    try {
      const blob = new Blob([content], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const win = window.open(url, '_blank', 'noopener,noreferrer')
      // Revoke after the new tab has had a chance to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      if (!win) {
        toast.error('Failed to open in browser', { description: 'Popup was blocked' })
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast.error('Failed to open in browser', { description: message })
    }
  }, [content])

  return (
    <button
      onClick={handleClick}
      title={`Open ${filePath.split('/').pop() ?? 'file'} in Browser`}
      className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] bg-background shadow-minimal text-xs font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0 select-none focus:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
    >
      <Globe className="w-3.5 h-3.5" />
      Open in Browser
    </button>
  )
}
